/**
 * DocumentDetails Component
 * 
 * Advanced PDF document viewer and AI-powered contract analysis interface
 * 
 * Features:
 * - PDF document viewing with markdown rendering
 * - Clause extraction and highlighting with fuzzy search algorithm
 * - AI-powered "Ask Genie" chatbot for document Q&A
 * - Dynamic clause mapping and extraction from contracts
 * - Real-time content highlighting with position-based search
 * - Multi-tab interface (Overview, Content)
 * - Collapsible clause summary with expandable details
 * - Markdown rendering with GFM and HTML support
 * - Responsive layout with sidebar clause panel
 * - Document metadata display (title, effective date, parties, etc.)
 * 
 * Key Algorithms:
 * - tokenizeWithPositions(): Converts text to tokens with position tracking
 * - calculateTokenMatchScore(): Scores token matches with gap penalties
 * - findFuzzyMatch(): Implements fuzzy search with configurable threshold
 * - findPhraseMatch(): Fallback phrase-based matching for short queries
 * - mapNormalizedToOriginal(): Maps normalized text positions to original indices
 * 
 * Dependencies:
 * - React (useState, useEffect, useRef hooks)
 * - React Router (useParams, useNavigate)
 * - React Markdown with remark-gfm and rehype-raw plugins
 * - Bootstrap components
 * - Lucide React icons
 * - jQuery (for DOM manipulation)
 * - Custom components: CommonIcon, PageLoader
 * - Custom hooks/utilities: apiCall, trackError, getMarkDownHtml
 * 
 * API Endpoints:
 * - GET /contracts/{documentid}/: Fetch document with PDF data and table information
 * - POST /contracts/pdf-highlighter/: Highlight specific text in PDF, returns page number
 * - POST /chat/: Send query to AI chatbot, returns answer
 * - getMarkDownHtml(documentid): Fetch document content as markdown
 * 
 * State Management:
 * - displayContent: Current markdown content to display
 * - selectedClause: Currently selected clause key from dynamicFields
 * - selectedClauseArrayIndex: Index within multi-value clause array
 * - expandedClause: Currently expanded clause in sidebar
 * - documents: Full document metadata and extracted fields
 * - data: API response data (alternative to documents)
 * - tabledata: Extracted table data from document
 * - filteredTableData: Filtered version of table data
 * - loading: Loading state for API calls
 * - input: Text input for Ask Genie chatbot
 * - conversationHistory: Array of chat messages (sent/replies)
 * - askgenieloading: Loading state for AI response
 * - iframeSrc: PDF iframe source URL
 * - activeTab: Currently active main tab (overview-tab, content)
 * - activeInnerTab: Currently active inner tab (show-clauses-tab, askgenie-tab)
 * - openItem: Currently open accordion item in clause list
 * 
 * @component
 * @returns {JSX.Element} Document viewer with multiple tabs and AI integration
 * 
 * @example
 * // Usage in routing
 * <Route path="/document-details/:documentid/:documentname/:type" element={<DocumentDetails />} />
 * 
 * @example
 * // Navigate to document viewer
 * navigate(`/document-details/${docId}/${docName}/view`);
 * navigate(`/document-details/${docId}/${docName}/askgenie`);
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams,useNavigate } from "react-router-dom";
import {API_SERVER_URL} from "../../utils/config_ip_details";
import {OVERVIEW_META_DATA} from "../../utils/config_mapping";
import {getFilenameFromURL,cutString,loader,capitalizeFirstLetterOfEachWord,encodeAndConstructFilename} from "../../utils/common_functions"
import {apiCall} from "../../utils/server_utils"
import CommonIcon from '../common/customicon';
import {trackError} from "../../utils/error_tracker"
// import PdfHighlighter from '../../utils/pdf-highlighter';
import {API_SUCCESS_RESPONSE_CODE} from "../../utils/config_mapping"
import $ from "jquery";
import PageLoader from '../common/pageloader';
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {getMarkDownHtml} from "../../services/admin"
import HeaderLogo  from "../structure/headerlogo";


// ========== SAMPLE DATA STRUCTURE ==========
/**
 * Sample contract document JSON structure for fallback/testing
 * Contains full contract agreement with extracted metadata, clauses, and AI analysis
 * 
 * Structure includes:
 * - Document metadata (title, status, dates, parties)
 * - Version history with change tracking
 * - Full markdown content
 * - Extracted clauses and dynamic fields
 * - AI-generated metadata and recommendations
 * - Processing status and file paths with SAS tokens
 * 
 * @type {Object}
 */
let samplejson = {
    "success": true,
    "data": {
        "_id": "68c685174432a2cdcf41bd60",
        "title": "Contract document",
        "status": "Draft",
        "created": "2025-09-14 09:04:23",
        "modified": "2025-09-14 09:04:23",
        "author": "Rajan",
        "priority": "medium",
        "template": "",
        "clauses": [],
        "tenantId": "687e0233bd7ce566f187ad5c",
        "filePath": "https://contractgenie.blob.core.windows.net/dev/Predactica/Contract_document.pdf",
        "fileType": "pdf",
        "fileSource": "localStorage",
        "type": "Agreement for Operational Services",
        "industry": "Education (University Services)",
        "version": "v1.0",
        "versions": [
            {
                "id": "d86861929993433a891c92cc841dcb45",
                "version": "v1.0",
                "date": "2025-09-14 09:05:31",
                "author": "Rajan",
                "change": "Initial upload",
                "content": "# This draft agreement is subject to change/fine tuning before final award of the contract\n\n---\n\n## (Sample Contract Agreement)  \n\n# AGREEMENT FOR HALL OF RESIDENCE NO. \u2013 __  \n\n**THIS AGREEMENT** has been made on this __th day of October, 2012 at IIT Kanpur  \n\n**BETWEEN** Indian Institute of Technology Kanpur (hereinafter referred to as the Institute) incorporated as a body of corporate under the Institute of Technology Act, 1961, through its Director (represented by Dean of Students\u2019 Affairs, Chairman, Council of Wardens & Warden-in-Charge/Warden of Hall of Residence No.- __)  \n\n**AND** M/s __________ registered under _______________________ and having its office at __________________________________________ (hereinafter referred to as The Service Provider which expression shall include his/their respective heirs, executors, administrators and assignees), represented by ____________________________ on the other part.  \n\n**AND** whereas the Institute has empanelled some agencies for providing operational services to its mess of Halls of Residence as per the terms and conditions, specifications and general conditions of the contract, as attached along with this agreement.  \n\n**AND** whereas the said terms and conditions, specifications as well as the scope of work to be done, as set out in the General Conditions of the contract, have been accepted and signed by the Service Provider.  \n\n---\n\n**AND** whereas the Service Provider has agreed to execute, upon and subject to the condition set forth herein, (hereinafter referred to as the said conditions) the work shown in the General Conditions of the Contract.  \n\n**AND** whereas the Service Provider has deposited a sum of Rs. __________ (___ lakhs only) as earnest money in the form of a *Demand Draft No. _______ dated _______* issued by ___________________________ in favour of Hall Management, IIT Kanpur payable at UBI, I.I.T. Branch Kanpur as performance guarantee for the said work and has agreed that this be retained by the Institute as part of Security Deposit of Rs. 5,00,000/- (Five Lakhs only).  \n\n**AND** whereas the Service Provider has promised to deposit Rs. __________ (_____ lakhs only) as remaining part of the security deposit in the form of a Bank Draft latest by ___________.  \n\n**AND** whereas in pursuance thereof the Service Provider has commenced the work with effect from October 01, 2012.  \n\n---\n\n**NOW** therefore it is hereby agreed as follows:  \n\n1. In consideration of the payments to be made to the Service Provider, as hereinafter provided and agreed to by both the parties, the Service Provider shall upon and subject to the said condition execute and complete the contract.  \n\n2. The Institute shall pay the Service Provider such sums as shall become payable hereunder at the time and in the manner specified in the said conditions.  \n\n3. The Quality of performance related to the work is the essence of the Contract and in the event of failure to perform as per term and conditions of the Contract and to the satisfaction of the Institute; the Service Provider shall be penalized as per provisions of the Contract.  \n\n4. The scope of work and prices Schedule of Quantities and conditions shall be according to the terms and conditions of this contract and the decision of the mutually agreed sole Arbitrator as appointed by the Deputy Director of the Institute, in reference to all matters of dispute in relation to this agreement or otherwise pertaining to the general condition of the contract shall be final and binding on both parties.  \n\n5. The said conditions and appendix thereto shall be read and constructed as forming part of this agreement and the parties hereto will respectively abide by and submit themselves to the conditions and perform the agreement on their parts respectively in such conditions contained.  \n\n6. All disputes arising out of or in any way connected with this agreement shall be deemed to have arisen in Kanpur and only courts in Kanpur shall have jurisdiction to determine the same.  \n\n7. The several parts of this contract have been read to us and fully understood by us. **IN WITNESS WHEREOF** the parties hereto have set their respective hands the day and the year herein above written.  \n\n---\n\n**Dean of Students\u2019 Affairs**  \n**Chairman, Council of Wardens**  \n**Warden, Hall of Residence No.-__**  \n\n_________________________  \nM/s ___________________  \n_________________________  \n(*On behalf of Service Provider*)  \n\n**In the presence of:**  \n1. _____________________________  \n2. _____________________________  \n\n---\n\n# GENERAL CONDITIONS OF THE CONTRACT  \n**FOR PROVIDING OPERATIONAL SERVICES IN THE MESS OF HALL OF RESIDENCE NO.-__**  \n\n---\n\n## ARTICLE \u2013 1  \n\n### 1.0 Definition of terms:  \n\nIn this Contract (as hereinafter defined) the following words and expressions shall have meanings hereby assigned to them, except where the context requires otherwise:  \n\n1.1 The **\"Contract\"** shall mean the agreement between the Institute and the service provider, duly signed by the parties to the Agreement, through their authorized representatives, for the execution of the work as described in the Scope of Work of this document and all terms and conditions mentioned herein after.  \n\n1.2 The **\"Service provider\"** shall mean the person or persons, the firm or company whose application for execution of work has been accepted by the Institute and includes the Service provider\u2019s legal representatives, his successors and permitted assignees.  \n\n1.3 The **\"Empanelment\"** shall mean enlistment of service provider for a limited period of 2 (Two) years, extendable at the end of the empanelment period at the discretion of the institute, for providing operational services in the messes of Halls of Residences on the campus of the Institute.  \n\n1.4 The **\"Hostel\"** shall mean the Hall of Residence No.- __ and the **\"Warden in charge\"** shall mean the person nominated by the Institute from time to time as warden incharge of the concerned hall of residence and shall include those who are expressly authorized by him/her to act on his/her behalf, for operation of this contract and supervision of work. The Warden-in-charge/Warden or such representatives shall have power to impose appropriate penalty in case there are violations of the provisions of the contract.  \n\n1.5 The **\"Institute\"** shall mean the Indian Institute of Technology Kanpur (IITK) with its premises located at Kalyanpur, Kanpur 208016 and shall include its authorized representatives, successors and assignees.  \n\n1.6 The **\"Letter of Empanelment\"** shall mean an official intimation from the Institute to empanelled agencies.  \n\n1.7 The **\"Hostel Executive Committee (HEC)\"** shall mean a committee of elected residents and hostel administration, which will be authorized to regulate the activities related to the mess of Hall\u2013__ on a day to day basis.  \n\n1.8 The **\"Work\"** shall mean and include all works to be executed, all items and things to be provided/done and services and activities to be performed by the service provider in accordance with the contract.  \n\n---\n\n## ARTICLE \u2013 2  \n\n### 2.0 Description of the Hall of Residence No.-__  \n\n2.1 The Hall of Residence No.-__ is a _____\u2019s Hostel situated inside the campus of the Institute with an exclusive and fully secured premise of its own. The hostel is fully accessible for transport. The Hostel comprises rooms for residents with a centrally located dining facility, administrative block, coffee shop, etc. The hostel is designed to accommodate a total of ___ students approximately with a minor variation.  \n\n2.2 The hostel residents generally stay on the premises and avail dining services throughout the academic year.  \n\n2.3 Scholars otherwise employed in Institute may also be allowed to stay in the hostel premises in addition to registered students. Such scholars shall also avail the mess facilities.  \n\n2.4 Generally the strength of the residents remains around **450** residents during the academic year; however, during the vacations this number may reduce substantially.  \n\n2.5 Day-Boarders, who do not stay in the hostel, may also be allowed to avail the dining facilities in Hall\u2013__ as outside members by the Warden-in-charge/Warden.  \n\n2.6 The hostel is provided with a self-contained centralized mess, comprising of kitchen and dining facility to prepare and serve meals to the residents and other authorized persons. The mess is well equipped with furniture, cooking facilities, utensils, appliances and equipment; etc.  \n\n2.7 The hostel is under the administration of a Warden in-charge/Warden who exercises overall control on all activities related to the hostel including dining services.  \n\n---\n\n## ARTICLE \u2013 3  \n\n### 3.0 Scope of Work:  \n\nThe contract is essentially for providing following messing services to the residents of Hall of Residence No.-__. The Scope of Work, covered by the contract, is broadly but not extensively described as given below:  \n\n- Cooking and serving three meals - breakfast, lunch and dinner. The hostel reserves the right to include the service for the preparation and serving of evening tea and light snacks in addition to these three meals. The terms and conditions of such an addition will be worked out as and when required;  \n- Facilitate procurement of raw material for and on behalf of the hostel;  \n- Managing and control of stocks and inventories;  \n- Coupon sales. Residents may use these coupons to get \u2018extra\u2019 items not included in the basic menu of the mess;  \n- Cleaning of utensils, kitchen and serving items;  \n- Cleaning of cooking, dining and auxiliary areas;  \n- Security of the equipment, utensils and other items in the mess;  \n- Maintenance of the equipment in the kitchen and dining areas;  \n- Maintenance of books, ledgers, other records and documents related to running of the mess;  \n- Deployment and supervision of required manpower for the above-mentioned tasks.  \n\n...\n\n*(Content continues as per the document with detailed subpoints, conditions, and tables.)*"
            }
        ],
        "content": "# This draft agreement is subject to change/fine tuning before final award of the contract\n\n---\n\n## (Sample Contract Agreement)  \n\n# AGREEMENT FOR HALL OF RESIDENCE NO. \u2013 __  \n\n**THIS AGREEMENT** has been made on this __th day of October, 2012 at IIT Kanpur  \n\n**BETWEEN** Indian Institute of Technology Kanpur (hereinafter referred to as the Institute) incorporated as a body of corporate under the Institute of Technology Act, 1961, through its Director (represented by Dean of Students\u2019 Affairs, Chairman, Council of Wardens & Warden-in-Charge/Warden of Hall of Residence No.- __)  \n\n**AND** M/s __________ registered under _______________________ and having its office at __________________________________________ (hereinafter referred to as The Service Provider which expression shall include his/their respective heirs, executors, administrators and assignees), represented by ____________________________ on the other part.  \n\n**AND** whereas the Institute has empanelled some agencies for providing operational services to its mess of Halls of Residence as per the terms and conditions, specifications and general conditions of the contract, as attached along with this agreement.  \n\n**AND** whereas the said terms and conditions, specifications as well as the scope of work to be done, as set out in the General Conditions of the contract, have been accepted and signed by the Service Provider.  \n\n---\n\n**AND** whereas the Service Provider has agreed to execute, upon and subject to the condition set forth herein, (hereinafter referred to as the said conditions) the work shown in the General Conditions of the Contract.  \n\n**AND** whereas the Service Provider has deposited a sum of Rs. __________ (___ lakhs only) as earnest money in the form of a *Demand Draft No. _______ dated _______* issued by ___________________________ in favour of Hall Management, IIT Kanpur payable at UBI, I.I.T. Branch Kanpur as performance guarantee for the said work and has agreed that this be retained by the Institute as part of Security Deposit of Rs. 5,00,000/- (Five Lakhs only).  \n\n**AND** whereas the Service Provider has promised to deposit Rs. __________ (_____ lakhs only) as remaining part of the security deposit in the form of a Bank Draft latest by ___________.  \n\n**AND** whereas in pursuance thereof the Service Provider has commenced the work with effect from October 01, 2012.  \n\n---\n\n**NOW** therefore it is hereby agreed as follows:  \n\n1. In consideration of the payments to be made to the Service Provider, as hereinafter provided and agreed to by both the parties, the Service Provider shall upon and subject to the said condition execute and complete the contract.  \n\n2. The Institute shall pay the Service Provider such sums as shall become payable hereunder at the time and in the manner specified in the said conditions.  \n\n3. The Quality of performance related to the work is the essence of the Contract and in the event of failure to perform as per term and conditions of the Contract and to the satisfaction of the Institute; the Service Provider shall be penalized as per provisions of the Contract.  \n\n4. The scope of work and prices Schedule of Quantities and conditions shall be according to the terms and conditions of this contract and the decision of the mutually agreed sole Arbitrator as appointed by the Deputy Director of the Institute, in reference to all matters of dispute in relation to this agreement or otherwise pertaining to the general condition of the contract shall be final and binding on both parties.  \n\n5. The said conditions and appendix thereto shall be read and constructed as forming part of this agreement and the parties hereto will respectively abide by and submit themselves to the conditions and perform the agreement on their parts respectively in such conditions contained.  \n\n6. All disputes arising out of or in any way connected with this agreement shall be deemed to have arisen in Kanpur and only courts in Kanpur shall have jurisdiction to determine the same.  \n\n7. The several parts of this contract have been read to us and fully understood by us. **IN WITNESS WHEREOF** the parties hereto have set their respective hands the day and the year herein above written.  \n\n---\n\n**Dean of Students\u2019 Affairs**  \n**Chairman, Council of Wardens**  \n**Warden, Hall of Residence No.-__**  \n\n_________________________  \nM/s ___________________  \n_________________________  \n(*On behalf of Service Provider*)  \n\n**In the presence of:**  \n1. _____________________________  \n2. _____________________________  \n\n---\n\n# GENERAL CONDITIONS OF THE CONTRACT  \n**FOR PROVIDING OPERATIONAL SERVICES IN THE MESS OF HALL OF RESIDENCE NO.-__**  \n\n---\n\n## ARTICLE \u2013 1  \n\n### 1.0 Definition of terms:  \n\nIn this Contract (as hereinafter defined) the following words and expressions shall have meanings hereby assigned to them, except where the context requires otherwise:  \n\n1.1 The **\"Contract\"** shall mean the agreement between the Institute and the service provider, duly signed by the parties to the Agreement, through their authorized representatives, for the execution of the work as described in the Scope of Work of this document and all terms and conditions mentioned herein after.  \n\n1.2 The **\"Service provider\"** shall mean the person or persons, the firm or company whose application for execution of work has been accepted by the Institute and includes the Service provider\u2019s legal representatives, his successors and permitted assignees.  \n\n1.3 The **\"Empanelment\"** shall mean enlistment of service provider for a limited period of 2 (Two) years, extendable at the end of the empanelment period at the discretion of the institute, for providing operational services in the messes of Halls of Residences on the campus of the Institute.  \n\n1.4 The **\"Hostel\"** shall mean the Hall of Residence No.- __ and the **\"Warden in charge\"** shall mean the person nominated by the Institute from time to time as warden incharge of the concerned hall of residence and shall include those who are expressly authorized by him/her to act on his/her behalf, for operation of this contract and supervision of work. The Warden-in-charge/Warden or such representatives shall have power to impose appropriate penalty in case there are violations of the provisions of the contract.  \n\n1.5 The **\"Institute\"** shall mean the Indian Institute of Technology Kanpur (IITK) with its premises located at Kalyanpur, Kanpur 208016 and shall include its authorized representatives, successors and assignees.  \n\n1.6 The **\"Letter of Empanelment\"** shall mean an official intimation from the Institute to empanelled agencies.  \n\n1.7 The **\"Hostel Executive Committee (HEC)\"** shall mean a committee of elected residents and hostel administration, which will be authorized to regulate the activities related to the mess of Hall\u2013__ on a day to day basis.  \n\n1.8 The **\"Work\"** shall mean and include all works to be executed, all items and things to be provided/done and services and activities to be performed by the service provider in accordance with the contract.  \n\n---\n\n## ARTICLE \u2013 2  \n\n### 2.0 Description of the Hall of Residence No.-__  \n\n2.1 The Hall of Residence No.-__ is a _____\u2019s Hostel situated inside the campus of the Institute with an exclusive and fully secured premise of its own. The hostel is fully accessible for transport. The Hostel comprises rooms for residents with a centrally located dining facility, administrative block, coffee shop, etc. The hostel is designed to accommodate a total of ___ students approximately with a minor variation.  \n\n2.2 The hostel residents generally stay on the premises and avail dining services throughout the academic year.  \n\n2.3 Scholars otherwise employed in Institute may also be allowed to stay in the hostel premises in addition to registered students. Such scholars shall also avail the mess facilities.  \n\n2.4 Generally the strength of the residents remains around **450** residents during the academic year; however, during the vacations this number may reduce substantially.  \n\n2.5 Day-Boarders, who do not stay in the hostel, may also be allowed to avail the dining facilities in Hall\u2013__ as outside members by the Warden-in-charge/Warden.  \n\n2.6 The hostel is provided with a self-contained centralized mess, comprising of kitchen and dining facility to prepare and serve meals to the residents and other authorized persons. The mess is well equipped with furniture, cooking facilities, utensils, appliances and equipment; etc.  \n\n2.7 The hostel is under the administration of a Warden in-charge/Warden who exercises overall control on all activities related to the hostel including dining services.  \n\n---\n\n## ARTICLE \u2013 3  \n\n### 3.0 Scope of Work:  \n\nThe contract is essentially for providing following messing services to the residents of Hall of Residence No.-__. The Scope of Work, covered by the contract, is broadly but not extensively described as given below:  \n\n- Cooking and serving three meals - breakfast, lunch and dinner. The hostel reserves the right to include the service for the preparation and serving of evening tea and light snacks in addition to these three meals. The terms and conditions of such an addition will be worked out as and when required;  \n- Facilitate procurement of raw material for and on behalf of the hostel;  \n- Managing and control of stocks and inventories;  \n- Coupon sales. Residents may use these coupons to get \u2018extra\u2019 items not included in the basic menu of the mess;  \n- Cleaning of utensils, kitchen and serving items;  \n- Cleaning of cooking, dining and auxiliary areas;  \n- Security of the equipment, utensils and other items in the mess;  \n- Maintenance of the equipment in the kitchen and dining areas;  \n- Maintenance of books, ledgers, other records and documents related to running of the mess;  \n- Deployment and supervision of required manpower for the above-mentioned tasks.  \n\n...\n\n*(Content continues as per the document with detailed subpoints, conditions, and tables.)*",
        "dynamicFields": {
            "Effective Date": "__th day of October, 2012",
            "Parties Involved": "Indian Institute of Technology Kanpur (Institute) and M/s __________ (Service Provider)",
            "Scope of Work": "Providing operational services in the mess of Hall of Residence No.-__",
            "Earnest Money Deposit": "Rs. __________ (___ lakhs only) in the form of Demand Draft",
            "Security Deposit": "Total Rs. 5,00,000/- (Five Lakhs only)",
            "Contract Duration": "Limited period of 2 (Two) years, extendable at discretion of the institute",
            "Jurisdiction": "Kanpur courts have jurisdiction over disputes",
            "Dispute Resolution": "Sole Arbitrator appointed by Deputy Director of the Institute",
            "Performance Obligation": "Quality of performance is the essence of the Contract",
            "Messing Services": "Cooking and serving meals, cleaning, procurement, maintenance, security",
            "Hostel Description": "Facility for approximately 450 residents with dining and administrative facilities",
            "Payment Terms": "Payments as specified in contract conditions"
        },
        "extractedClauses": {
            "Effective Date": "__th day of October, 2012",
            "Parties Involved": "Indian Institute of Technology Kanpur (Institute) and M/s __________ (Service Provider)",
            "Scope of Work": "Providing operational services in the mess of Hall of Residence No.-__",
            "Earnest Money Deposit": "Rs. __________ (___ lakhs only) in the form of Demand Draft",
            "Security Deposit": "Total Rs. 5,00,000/- (Five Lakhs only)",
            "Contract Duration": "Limited period of 2 (Two) years, extendable at discretion of the institute",
            "Jurisdiction": "Kanpur courts have jurisdiction over disputes",
            "Dispute Resolution": "Sole Arbitrator appointed by Deputy Director of the Institute",
            "Performance Obligation": "Quality of performance is the essence of the Contract",
            "Messing Services": "Cooking and serving meals, cleaning, procurement, maintenance, security",
            "Hostel Description": "Facility for approximately 450 residents with dining and administrative facilities",
            "Payment Terms": "Payments as specified in contract conditions"
        },
        "aiScores": {
            "risk": "Medium",
            "coverage": 80,
            "confidence": 85,
            "attention": 70
        },
        "metaData": {
            "startDate": "2012-10-01",
            "contractAmount": "Rs. 5,00,000/- Security Deposit",
            "contractParty1": "Indian Institute of Technology Kanpur",
            "contractParty2": "M/s __________",
            "governingLaw": "Kanpur",
            "isSigned": false
        },
        "aiRecommendations": {
            "coverage": [
                "Include specific terms for renewal period conditions.",
                "Ensure detailed payment schedule and penalties for late payment are included."
            ],
            "risk": [
                "Clarify penalty provisions in case of performance failure.",
                "Include additional safeguards for dispute resolution beyond sole arbitration."
            ],
            "confidence": [
                "Ensure precise identification of the Service Provider entity.",
                "Verify completeness of financial terms like security deposit timelines."
            ]
        },
        "graphProcessed": true,
        "graphProcessedAt": "2025-09-14 09:08:06",
        "filePathWithSasToken": "https://contractgenie.blob.core.windows.net/dev/Predactica/Contract_document.pdf?se=2025-10-12T16%3A42%3A46Z&sp=r&sv=2023-11-03&sr=b&rscd=inline&rsct=application/pdf&sig=%2B4n2Ht1ec3Wgi9BHNiA05i3p7p4LDy4rTVQ8jvgc8eM%3D"
    },
    "error": null,
    "code": 200
}

export const DocumentDetails = () => {
  // ========== NAVIGATION & ROUTING ==========
  /** React Router navigation hook for programmatic navigation */
  const navigate = useNavigate();

  /**
   * Navigate back to document list page
   * @function
   * @returns {void}
   */
  const gobacktodoucment = (event) => {
    navigate("/document-list")
  }

  // ========== MAIN STATE MANAGEMENT ==========
  /** Current document content in markdown format for display */
  const [displayContent, setDisplayContent] = useState(samplejson.data.content);
  
  /** Currently selected clause key from document.dynamicFields for highlighting */
  const [selectedClause, setSelectedClause] = useState();
  
  /** Index within multi-value clause arrays for highlighting specific values */
  const [selectedClauseArrayIndex, setSelectedClauseArrayIndex] = useState();
  
  /** Currently expanded clause in sidebar accordion (null = all collapsed) */
  const [expandedClause, setExpandedClause] = useState(null);
  
  /** Complete document object with metadata, content, and extracted data */
  const [documents, setDocuments] = useState(samplejson.data);

  // ========== URL PARAMETERS ==========
  /** Document ID from URL parameter - used to fetch document from API */
  const {documentid,documentname,type} = useParams(); // Extract the `id` parameter from the URL
  
  /** API fetched document data (alternative to documents state) */
  const [data, setData] = useState(""); // State to hold API data
  
  /** Table data extracted from document structure */
  const [tabledata, setTableData] = useState([]); // State to hold API data
  
  /** Filtered/processed version of table data */
  const [filteredTableData, setFilteredTableData] = useState([]);
  
  /** Loading state for document fetch operation */
  const [loading, setLoading] = useState(true); // State to track loading

  // ========== ASK GENIE CHATBOT STATE ==========
  /** Current user input for Ask Genie textarea */
  const [input, setInput] = useState('');
  
  /** Array of conversation messages with type ('sent' or 'replies') and message text */
  const [conversationHistory, setConversationHistory] = useState([]);
  
  /** Loading state for AI response processing */
  const [askgenieloading, setAskgenieLoading] = useState(false); // State for loader
  
  /** Reference to chat messages container for auto-scroll functionality */
  const messagesContainerRef = useRef(null); // Reference to the chat message container

  // ========== TAB MANAGEMENT ==========
  /** Active main tab ID: 'overview-tab' (metadata) or 'content' (viewer) */
  const [activeTab, setActiveTab] = useState('overview-tab');  // State for active tab
  
  /**
   * Handle main tab change
   * @function
   * @param {string} tabId - Tab identifier ('overview-tab' or 'content')
   * @returns {void}
   */
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // ========== INNER TAB MANAGEMENT ==========
  /** Active inner tab ID: 'show-clauses-tab' or 'askgenie-tab' */
  const [activeInnerTab, setActiveInnerTab] = useState('show-clauses-tab');
  
  /**
   * Handle inner tab change
   * @function
   * @param {string} tab - Tab identifier ('show-clauses-tab' or 'askgenie-tab')
   * @returns {void}
   */
  const handleInnerTabChange = (tab) => {
    setActiveInnerTab(tab);
  };

  // ========== ACCORDION STATE ==========
  /** Currently open accordion item in clause list (null = all collapsed, only one can be open) */
  const [openItem, setOpenItem] = useState(null); // Track only one open item
  
  /**
   * Toggle accordion open/closed state
   * Opens clicked item, closes all others (single-open pattern)
   * @function
   * @param {string} key - Clause key to toggle
   * @returns {void}
   */
  const toggleAccordion = (key) => {
    setOpenItem(prevKey => (prevKey === key ? null : key)); // Open clicked item, close others
  };

  // ========== PDF VIEWER STATE ==========
  /** PDF iframe source URL for document viewing (constructed from API response) */
  const [iframeSrc, setIframeSrc] = useState("");


  // ========== FUZZY SEARCH ALGORITHM FUNCTIONS ==========
  /**
   * Tokenize text into individual tokens with position information
   * Splits on whitespace, tracks original text, start and end positions
   * 
   * Algorithm:
   * 1. Use regex to find all word matches
   * 2. Store token: cleaned/lowercase version
   * 3. Store originalToken: original case and punctuation
   * 4. Store start/end: character positions in original text
   * 
   * @function
   * @param {string} text - Text to tokenize
   * @returns {Array<Object>} Array of token objects
   * @example
   * tokenizeWithPositions("Hello World!")
   * // Returns: [
   * //   { token: "hello", originalToken: "Hello", start: 0, end: 5 },
   * //   { token: "world", originalToken: "World!", start: 6, end: 12 }
   * // ]
   */
  function tokenizeWithPositions(text) {
    if (!text) return [];
    const tokens = [];
    const regex = /\S+/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      tokens.push({
        token: match[0].toLowerCase().replace(/[^a-z0-9]/g, ''),
        originalToken: match[0],
        start: match.index,
        end: match.index + match[0].length
      });
    }
    return tokens;
  }

  // ========== STOP WORDS FILTER ==========
  /**
   * Set of common English words to ignore in fuzzy matching
   * These words have low semantic value and should not affect search score
   * Includes: articles, prepositions, conjunctions, modal verbs
   * 
   * @type {Set<string>}
   */
  const STOP_WORDS = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
    'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
    'to', 'was', 'were', 'will', 'with', 'or', 'shall', 'any', 'all'
  ]);

  /**
   * Calculate match score for token window against search tokens
   * Implements advanced scoring with:
   * - Base score: matched tokens / meaningful search tokens
   * - Gap penalty: penalizes tokens not in sequence (gap > maxGap)
   * - Density bonus: rewards tight clustering of matches
   * 
   * Scoring formula: baseScore - gapPenalty + (densityBonus * 0.2)
   * Where:
   * - baseScore = matchCount / meaningfulSearchTokens.length
   * - gapPenalty = totalGap * 0.1 (penalizes each token gap over maxGap)
   * - densityBonus = matchCount / windowTokens.length (rewards concentrated matches)
   * 
   * @function
   * @param {Array<Object>} searchTokens - Tokens from search query
   * @param {Array<Object>} windowTokens - Tokens from candidate text window
   * @param {number} [maxGap=5] - Maximum gap between matches before penalty applies
   * @returns {number} Match score from 0 to ~1.0+ (values >1.0 possible with high density)
   * 
   * @example
   * // Searching for "effective date" in window with those exact words
   * // Returns high score (>0.8)
   * 
   * @example
   * // Searching for "party agreement" with 5 tokens between them
   * // Returns lower score due to gap penalty
   */
  function calculateTokenMatchScore(searchTokens, windowTokens, maxGap = 5) {
    if (searchTokens.length === 0 || windowTokens.length === 0) return 0;
    const meaningfulSearchTokens = searchTokens.filter(t => t.token.length > 2 && !STOP_WORDS.has(t.token));
    const meaningfulWindowTokens = windowTokens.filter(t => t.token.length > 2 && !STOP_WORDS.has(t.token));
    if (meaningfulSearchTokens.length === 0) {
      return calculateSimpleTokenMatch(searchTokens, windowTokens) * 0.3;
    }
    let matchCount = 0;
    let totalGap = 0;
    const searchSet = new Set(meaningfulSearchTokens.map(t => t.token));
    const matchedPositions = [];
    windowTokens.forEach((token, index) => {
      if (searchSet.has(token.token)) {
        matchCount++;
        matchedPositions.push(index);
      }
    });
    if (matchCount === 0) return 0;
    if (matchedPositions.length > 1) {
      for (let i = 1; i < matchedPositions.length; i++) {
        const gap = matchedPositions[i] - matchedPositions[i-1] - 1;
        totalGap += Math.max(0, gap - maxGap);
      }
    }
    const baseScore = matchCount / meaningfulSearchTokens.length;
    const gapPenalty = totalGap * 0.1;
    const densityBonus = matchCount / windowTokens.length;
    return Math.max(0, baseScore - gapPenalty + (densityBonus * 0.2));
  }

  /**
   * Simple token matching for fallback cases
   * Used when meaningful tokens cannot be found
   * 
   * @function
   * @param {Array<Object>} searchTokens - Tokens from search query
   * @param {Array<Object>} windowTokens - Tokens from candidate window
   * @returns {number} Ratio of matched tokens to search tokens (0 to 1.0)
   */
  function calculateSimpleTokenMatch(searchTokens, windowTokens) {
    const searchSet = new Set(searchTokens.map(t => t.token));
    const matches = windowTokens.filter(t => searchSet.has(t.token)).length;
    return matches / searchTokens.length;
  }

  /**
   * Find fuzzy match in content for given search text
   * Implements multi-stage matching strategy:
   * 1. Try exact string match first
   * 2. Try sliding window token matching at multiple scales
   * 3. Fallback to phrase matching if no good match found
   * 
   * Window sizes: from minWindowSize (searchTokens.length) to maxWindowSize (searchTokens.length * 3)
   * Adjusted to stay within content bounds (3 to 50 tokens)
   * 
   * @function
   * @param {string} content - Full text content to search in
   * @param {string} searchText - Text to search for
   * @param {number} [threshold=0.6] - Minimum match score to return result
   * @returns {Object|null} Match object with {start, end, score} positions or null if no match
   * 
   * @example
   * // Find "effective date" in contract text
   * const match = findFuzzyMatch(contractContent, "effective date");
   * if (match) {
   *   const highlighted = contractContent.substring(0, match.start) + 
   *                       "<span>" + contractContent.substring(match.start, match.end) + "</span>" +
   *                       contractContent.substring(match.end);
   * }
   */
  function findFuzzyMatch(content, searchText, threshold = 0.6) {
    if (!searchText || !content) return null;
    searchText = String(searchText);
    const contentTokens = tokenizeWithPositions(content);
    const searchTokens = tokenizeWithPositions(searchText);
    if (searchTokens.length === 0) return null;
    const normalizedContent = content.toLowerCase().replace(/\s+/g, ' ').trim();
    const normalizedSearch = searchText.toLowerCase().replace(/\s+/g, ' ').trim();
    const exactIndex = normalizedContent.indexOf(normalizedSearch);
    if (exactIndex !== -1) {
      return mapNormalizedToOriginal(content, normalizedContent, exactIndex, normalizedSearch.length);
    }
    let bestMatch = null;
    let bestScore = 0;
    const maxWindowSize = Math.min(Math.max(searchTokens.length * 3, 50), contentTokens.length);
    const minWindowSize = Math.max(searchTokens.length, 3);
    for (let windowSize = minWindowSize; windowSize <= maxWindowSize; windowSize++) {
      for (let i = 0; i <= contentTokens.length - windowSize; i++) {
        const windowTokens = contentTokens.slice(i, i + windowSize);
        const score = calculateTokenMatchScore(searchTokens, windowTokens);
        if (score > bestScore && score >= threshold) {
          bestScore = score;
          bestMatch = {
            start: contentTokens[i].start,
            end: contentTokens[i + windowSize - 1].end,
            score: score
          };
        }
      }
    }
    if (!bestMatch && threshold > 0.4) {
      return findFuzzyMatch(content, searchText, 0.4);
    }
    if (!bestMatch && searchTokens.length <= 5) {
      return findPhraseMatch(content, searchText, 0.3);
    }
    return bestMatch;
  }

  /**
   * Fallback phrase-based matching algorithm
   * Used when token matching fails, searches for individual keyword presence
   * Scores sentences by word coverage ratio
   * 
   * @function
   * @param {string} content - Full text content to search in
   * @param {string} searchText - Text to search for
   * @param {number} [threshold=0.3] - Minimum match score (words found / total words)
   * @returns {Object|null} Best matching sentence span with {start, end, score}
   * 
   * @example
   * // Find "contract duration" even with rephrased text
   * const match = findPhraseMatch(content, "duration");
   * // Will find sentence mentioning "duration" even if "contract" is absent
   */
  function findPhraseMatch(content, searchText, threshold = 0.3) {
    const words = searchText.toLowerCase().split(/\s+/).filter(w => w.length > 2);
    if (words.length === 0) return null;
    const contentLower = content.toLowerCase();
    let bestMatch = null;
    let bestScore = 0;
    const sentences = content.split(/[.!?]\s*/);
    sentences.forEach(sentence => {
      const sentenceLower = sentence.toLowerCase();
      const wordsFound = words.filter(word => sentenceLower.includes(word));
      const score = wordsFound.length / words.length;
      if (score > bestScore && score >= threshold) {
        bestScore = score;
        const sentenceStart = content.indexOf(sentence);
        bestMatch = {
          start: sentenceStart,
          end: sentenceStart + sentence.length,
          score: score
        };
      }
    });
    return bestMatch;
  }

  /**
   * Map normalized text position to original text position
   * Handles whitespace differences between normalized and original text
   * Used to convert fuzzy match results to original document positions
   * 
   * Algorithm:
   * 1. Iterate through both original and normalized text simultaneously
   * 2. Skip whitespace in original when encountered
   * 3. Match normalized positions to original indices
   * 4. Return start and end positions in original text
   * 
   * @function
   * @param {string} original - Original text with whitespace
   * @param {string} normalized - Normalized text (whitespace collapsed)
   * @param {number} startPos - Start position in normalized text
   * @param {number} length - Length of match in normalized text
   * @returns {Object} Match span {start, end, score: 1.0} in original text
   */
  function mapNormalizedToOriginal(original, normalized, startPos, length) {
    let origIndex = 0;
    let normIndex = 0;
    let startFound = false;
    let origStart = 0;
    while (origIndex < original.length && normIndex <= startPos + length) {
      const origChar = original[origIndex];
      if (/\s/.test(origChar)) {
        if (normIndex === startPos && !startFound) {
          origStart = origIndex;
          startFound = true;
        }
        origIndex++;
        if (normIndex < normalized.length && /\s/.test(normalized[normIndex])) {
          normIndex++;
        }
      } else {
        if (normIndex === startPos && !startFound) {
          origStart = origIndex;
          startFound = true;
        }
        if (normIndex >= startPos + length) {
          return {
            start: origStart,
            end: origIndex,
            score: 1.0
          };
        }
        origIndex++;
        normIndex++;
      }
    }
    return {
      start: origStart,
      end: origIndex,
      score: 1.0
    };
  }

  /**
   * Highlight specific content in the document
   * Applies CSS classes and HTML spans to highlight matched text
   * Uses exact regex match first, falls back to fuzzy match
   * 
   * @function
   * @param {string} content - Full document content to highlight
   * @param {Object} fields - Document dynamic fields/clauses object
   * @param {string} selectedKey - Key of clause to highlight
   * @param {number} selectedClauseArrayIndexs - Index in multi-value clause array
   * @returns {string} HTML string with highlighted matches
   * 
   * @example
   * // Highlight "effective date" in contract
   * const highlighted = highlightContent(
   *   contractContent, 
   *   document.dynamicFields, 
   *   "Effective Date", 
   *   0
   * );
   * // Returns HTML with <span class="bg-warning"> around matched text
   */
  function highlightContent(content, fields, selectedKey,selectedClauseArrayIndexs) {
    if (!content || !fields) return content;
    let highlighted = content;
    if (selectedKey && fields[selectedKey][selectedClauseArrayIndex]) {
      const searchValue = fields[selectedKey][selectedClauseArrayIndex];
      //alert("searchValue:"+searchValue)
      let matchFound = false;
      try {
        const regex = new RegExp(searchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
        if (regex.test(content)) {
          highlighted = content.replace(regex, (match) => {
            return `<span id="field-${selectedKey+"_"+selectedClauseArrayIndex}" class="bg-warning px-1 rounded">${match}</span>`;
          });
          matchFound = true;
        }
      } catch (e) {}
      if (!matchFound) {
        const match = findFuzzyMatch(content, searchValue);
        if (match) {
          const before = content.substring(0, match.start);
          const matchText = content.substring(match.start, match.end);
          const after = content.substring(match.end);
highlighted = before + `<span id="field-${selectedKey+"_"+selectedClauseArrayIndex}" class="bg-warning px-1 rounded">${matchText}</span>` + after;
          matchFound = true;
        }
      }
      if (!matchFound) {
        console.log(`No match found for clause: ${selectedKey}`);
        // console.log(`Search text: ${searchValue.substring(0, 100)}...`);
        // console.log(`Content length: ${content.length} characters`);
      } else {
        console.log(`Match found for clause: ${selectedKey}`);
      }
    }
    return highlighted;
  }

  /**
   * Handle clause expand/collapse in sidebar
   * Toggles expanded state of specific clause
   * 
   * @function
   * @param {string} key - Clause key to toggle
   * @returns {void}
   */
  // Expand/Collapse handler
  const handleExpandToggle = (key) => {
    setExpandedClause((prevKey) => (prevKey === key ? null : key));
  };


  // ========== API INTEGRATION FUNCTIONS ==========
  /**
   * Highlight word/phrase in PDF and get page number
   * Calls backend PDF highlighter API to find text position in PDF
   * Updates iframe source to show highlighted content
   * 
   * API Endpoint: POST /contracts/pdf-highlighter/
   * Request: { pdf_url: string, sentence: string }
   * Response: { highlighted_pdf_url: string, page_no: number }
   * 
   * @async
   * @function
   * @param {string} pmText - Text/sentence to highlight in PDF
   * @returns {Promise<void>}
   * 
   * @example
   * // Highlight effective date in PDF
   * await highlightWord("effective date: October 1, 2012");
   * // Updates iframeSrc to show PDF at correct page with highlighting
   */
   // Fetch data from the API
   const highlightWord = async (pmText) => {
       let request_object = {}
       request_object.pdf_url = data["FilePath"]
       request_object.sentence = pmText
         let apiUrl = API_SERVER_URL+ 'contracts/pdf-highlighter/'
         let method = "POST"
         try {
             const result = await apiCall(method,apiUrl,request_object);
             if(result!==undefined){
             if (result.code === API_SUCCESS_RESPONSE_CODE) {
              //&zoom=75&toolbar=0&navpanes=0
               setIframeSrc(encodeAndConstructFilename(result.data.highlighted_pdf_url)+"#page="+result.data.page_no);
             } else {
               trackError("The API response is invalid:<br/>URL:"+apiUrl+"<br/>Method:"+method+"<br>", JSON.stringify(result))
             }
             }
         } catch (error) {
           console.error("Error refreshing token:", error);
         }
 }

  // ========== DOCUMENT FETCH EFFECT HOOK ==========
  /**
   * Effect Hook: Fetch document content and metadata on component mount
   * 
   * Data Fetching Strategy:
   * 1. First call: getMarkDownHtml() - Get markdown content and metadata
   * 2. Set loading state based on fetch completion
   * 3. Update document state with fetched data
   * 4. Handle errors and set loading state
   * 5. Optional: Auto-navigate to specific tab if type='askgenie'
   * 
   * Dependencies: [] (run once on mount)
   * 
   * Endpoints Called:
   * - getMarkDownHtml(documentid): Fetch document with markdown content
   * 
   * State Updates:
   * - data: API response document object
   * - documents: Document metadata and content
   * - displayContent: Markdown content for viewer
   * - loading: false after fetch completes
   * 
   * @type {React.EffectHook}
   */
  useEffect(() => {

    const fetchData = async () => {
      $(".page_pro_load").hide();
      setLoading(true); // Stop loading spinner
      try {
        const result = await getMarkDownHtml(documentid);
        if (result.code === API_SUCCESS_RESPONSE_CODE) {
          setData(result.data);
          setDocuments(result.data);
          setDisplayContent(result.data.content);
         
        } else {
          console.warn("Unexpected response status:", result.status);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      finally {
         setLoading(false); // Stop loading spinner
     }
    };

    
   const fetchData__ = async () => {
      try {
        const apiUrl = ""
        const method = "" 
        const result = await apiCall("GET", `${API_SERVER_URL}contracts/`+documentid+`/`);
        if(result!==undefined){
        if (result.code === API_SUCCESS_RESPONSE_CODE) {
          setData(result.data);
          setIframeSrc(encodeAndConstructFilename(result.data.pdf_temp_url));
          if (result.data && result.data.table_data && result.data.table_data.length > 0){
               setTableData(result.data.table_data);
               setFilteredTableData(result.data.table_data);
          }
        } else {
          trackError("The API response is invalid:<br/>URL:"+apiUrl+"<br/>Method:"+method+"<br>", JSON.stringify(result))
        }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }finally {
         setLoading(false); // Stop loading spinner
         if(type === "askgenie"){
            const timer = setTimeout(() => {
               handleTabChange('content')
               handleInnerTabChange('askgenie-tab')
             }, 500);

           }  
      }
    };
    fetchData();
   // Fetch data from the API
   //  const fetchData = async () => {
   //    try {
   //      const response = await fetch(API_SERVER_URL+"documents/"); // Replace with your API URL
   //      const result = await response.json();
   //      if (result.status === "success") {
   //        setData(result.data[0]);
   //      }
   //    } catch (error) {
   //      setLoading(false); // Set loading to false after data is fetched
   //      console.error("Error fetching data:", error);
   //    } finally {
   //      setLoading(false); // Stop loading spinner
   //    }
   //  };
   //  fetchData();

  }, []); // Empty dependency array ensures this runs only once


 
  useEffect(() => {
   // Fetch data from the API
   const handleAakGenie = async () => {
       let request_object = {}
       request_object.doc_id = "doc-boofv70zg"
       request_object.query = "What is expiry date"
      
         let apiUrl = API_SERVER_URL+ 'chat/'
         let method = "POST"
         try {
             const result = await apiCall(method,apiUrl,request_object);
             if(result!==undefined){
             if (result.code === API_SUCCESS_RESPONSE_CODE) {
                
             } else {
               trackError("The API response is invalid:<br/>URL:"+apiUrl+"<br/>Method:"+method+"<br>", JSON.stringify(result))
             }
             }
         } catch (error) {
           console.error("Error refreshing token:", error);
         }
       
 };
 //handleAakGenie();
},[])

  useEffect(() => {

   // Fetch data from the API
    const fetchPdfPageInfo = async () => {
      try {
         const apiUrl = ""
         const method = "" 
         const result = await apiCall("GET", `${API_SERVER_URL}contracts/`+documentid+`/pdf-info/`);  
         if (result.status === "success") {
            // setData(result.data[0]);
         }
      } catch (error) {
      }
    };
    //fetchPdfPageInfo();
  },[])

  useEffect(() => {
  return;
  
    if (filteredTableData.length > -1) {
     // Destroy the existing DataTable if it exists
     const table = $("#contract_grid").DataTable({
       data: filteredTableData,
       destroy: true, // Ensure the previous table is destroyed
       columns: [
         { data: "Documentname", title: "Document Name", width: "20px" },
         { data: "Properties", title: "Properties", width: "20px" },
         { data: "Categories", title: "Categories", width: "10px" },
         { data: "SubCategories", title: "SubCategories", width: "10px" },
         { data: "Channels", title: "Channels", width: "10px" },
         { data: "Teritory", title: "Teritory", width: "10px" },
         { data: "UserGroups", title: "UserGroups", width: "10px" },
       ],
       paging: false,
       searching: false,
       ordering: true,
       responsive: true,
       fixedHeader: true,
       scrollY: "300px",  // Set the fixed height for vertical scroll
       scrollCollapse: true,  // Collapse the height if less data
       language: {
         emptyTable: "No data found matching the filters",  // Custom message for no data
       },
     });

   // Use ResizeObserver to detect changes in div size
   const container = document.getElementById("contract_grid");
   const resizeObserver = new ResizeObserver(() => {
     table.columns.adjust().draw();  // Adjust and redraw table on div resize
   });

   // Start observing the size changes of the div
   if (container) {
     resizeObserver.observe(container);
   }

   // Cleanup on unmount
   return () => {
     table.destroy();  // Clean up DataTable instance
     if (container) {
       resizeObserver.unobserve(container);  // Stop observing the div
     }
   };
 }
 }, [filteredTableData]);


 
useEffect(() => {
  if (selectedClause !== null) {
    const element = document.getElementById(`field-${selectedClause+"_"+selectedClauseArrayIndex}`);
    const existingElements = document.querySelectorAll('.border-warning'); // Bootstrap highlight class

    if (existingElements) {
      existingElements.forEach(el => {
        el.classList.remove('border', 'border-warning');
        el.style.backgroundColor = '';
      });
    }

    if (element) {
      // alert("element:"+element) 
      element.classList.remove('border', 'border-warning');
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('border', 'border-warning');
      // element.style.backgroundColor = '#fff3cd'; // Bootstrap warning-subtle color
    }
  }
}, [selectedClause,selectedClauseArrayIndex]);



 //#- Ask Genie 
 /**
  * Handle sending a message to Ask Genie AI chatbot
  * 
  * Process:
  * 1. Validate input is not empty
  * 2. Add user message to conversation history
  * 3. Send query to AI chat API
  * 4. Display AI response in chat
  * 5. Show loader while waiting for response
  * 6. Clear input field after sending
  * 
  * API Endpoint: POST /chat/
  * Request: { doc_id: string, query: string }
  * Response: { answer: string }
  * 
  * @async
  * @function
  * @returns {Promise<void>}
  * 
  * @example
  * // User types "What is the contract duration?" and clicks send
  * handleSendMessage();
  * // Displays message in chat, shows loader, AI responds with answer
  */
 const handleSendMessage = async () => {
   if (!input.trim()) return;

   const userMessage = { type: 'sent', message: input };
   setConversationHistory([...conversationHistory, userMessage]);

   let request_object = {
       doc_id: data["DocumentNo"], // Replace with actual document ID
       query: input,
   };

   let apiUrl = `${API_SERVER_URL}chat/`;
   let method = "POST";
   setAskgenieLoading(true); // Show loader while waiting for the response

   try {
       const result = await apiCall(method, apiUrl, request_object);
       setAskgenieLoading(false); // Hide loader after getting the response
       if (result && result.code === API_SUCCESS_RESPONSE_CODE) {
           const apiAnswer = result.data.answer || "No answer found.";
           const apiResponse = { type: 'replies', message: apiAnswer };
           setConversationHistory([...conversationHistory, userMessage, apiResponse]);
       } else {
           console.error("Invalid API response", result);
       }

   } catch (error) {
       setLoading(false); // Hide loader if there's an error
       console.error("Error fetching data:", error);
   }
   setInput('');
};


useEffect(() => {
  return;

  /**
   * Initialize Bootstrap tooltips on elements with data-bs-toggle="tooltip"
   * Tooltips show contextual help when hovering over UI elements
   * 
   * Note: This effect is currently disabled (returns early)
   * Re-enable if tooltip functionality is needed
   * 
   * @type {React.EffectHook}
   */
  const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
  tooltipTriggerList.map((tooltipTriggerEl) => new window.bootstrap.Tooltip(tooltipTriggerEl));
  // Cleanup tooltips on component unmount
  return () => {
    tooltipTriggerList.map((tooltipTriggerEl) => {
      const tooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
      if (tooltip) tooltip.dispose();
    });
  };
}, []);

/**
 * Effect Hook: Auto-scroll chat messages to latest message
 * Scrolls messages container to bottom when conversation history updates
 * Ensures latest messages are visible without user scrolling
 * 
 * Dependencies: [conversationHistory] (triggers on new messages)
 * 
 * @type {React.EffectHook}
 */
useEffect(() => {
  if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
  }
}, [conversationHistory]);


/**
 * Handle clause click in sidebar for highlighting
 * Sets selectedClause and index to trigger content highlighting
 * 
 * @function
 * @param {string} clauseKey - Key/name of clause from document.dynamicFields
 * @param {integer} classIndex - Index within multi-value clause array
 * @returns {void}
 * 
 * @example
 * // Click "Effective Date" clause with first value
 * handleClauseClick("Effective Date", 0);
 * // Highlights that text in the document viewer
 */
const handleClauseClick = (clauseKey: string,classIndex:integer) => {
// alert(clauseKey)
// alert(classIndex)
setSelectedClause(clauseKey);
setSelectedClauseArrayIndex(classIndex);
// In a real app, this would highlight the section in the PDF viewer
console.log(`Highlighting clause: ${clauseKey}`);
};


  // ========== MAIN COMPONENT RENDER ==========
  /**
   * Main JSX Return Structure:
   * 
   * Layout Architecture (3-level nesting):
   * 1. Main Container: div.main-content > div.page-content > div.container-fluid
   * 2. Page Header: Page title "Contract Intelligence" with breadcrumb navigation
   * 3. Tab Navigation: Two main tabs (Overview, Content)
   * 4. Tab Content:
   *    a) Overview Tab:
   *       - Contract metadata cards (8 fields): title, document no, type, parties, version, dates, period
   *       - Contract details table (if data exists)
   *    b) Content Tab (2-column layout):
   *       - Left (col-lg-8): Document viewer with markdown rendering
   *       - Right (col-lg-4): Clause summary sidebar with expandable accordion
   * 5. Inner Tabs (hidden, for future use):
   *    - Show Clauses Tab: Accordion-based clause listing
   *    - Ask Genie Tab: AI chatbot interface with message history
   * 
   * Responsive Design:
   * - Sidebar switches to full-width on mobile (md breakpoint)
   * - Tab navigation adapts for small screens
   * - Document viewer adjusts height based on screen size
   * 
   * Loading State:
   * - Shows PageLoader component while fetching document
   * - Shows loader in chat while waiting for AI response
   * 
   * Empty States:
   * - No contract details table if table_data is empty
   * - Ask Genie shows welcome message when no conversation history
   * - "No PDF URL Found" if iframeSrc is empty
   * 
   * @returns {JSX.Element} Complete document viewer interface
   */
  return (
        <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
        <div className="page-content">
            <div className="container-fluid">
           <>
                <div className="flex-grow-1">
                    <div className="page-title">Contract Intelligence</div>

                    {/* Breadcrumb Navigation */}
                    <nav aria-label="breadcrumb">
  <ol className="breadcrumb bg-white p-2 rounded">
    {/* Back button to document list */}
    <span onClick={() => navigate("/document-list")}   style={{ color: 'blue', cursor: 'pointer' }}
>
        ← Back 
      </span> &nbsp;&nbsp;
    {/* Current document breadcrumb */}
    <li className="breadcrumb-item">
      Contract <svg
  xmlns="http://www.w3.org/2000/svg"
  width="15"
  height="15"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="lucide lucide-chevron-right-icon lucide-chevron-right"
>
  <path d="m9 18 6-6-6-6" />
</svg>
{data !=="" ? getFilenameFromURL(data["FilePath"]) : ""}
    </li>
  </ol>
</nav>

                </div>
                {/* Error message container */}
                <div id="app_err"></div>

                {/* Loading State or Content Display */}
                {loading === true ? (
                 <PageLoader />
            ) :  data !=="" ?
                (
                <>
               {/* Main Tab Navigation */}
               <div className="d-flex mt-4">
               <div className="card">
               <div className="card-body" style={{padding:"5px"}}>
               {/* Tab List: Overview and Content tabs */}
               <ul className="nav nav-pills animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1" role="tablist">
                {/* Overview Tab: Document metadata */}
                <li className="nav-item">
                   <a             className={`nav-link fs-14 ${activeTab === 'overview-tab' ? 'active' : ''}`} 
            data-bs-toggle="tab" 
            href="javascript:void(0)" 
            role="tab"
            onClick={() => handleTabChange('overview-tab')}
>
                   <i className="ri-airplay-fill d-inline-block d-md-none"></i> <span className="d-none d-md-inline-block"><CommonIcon iconName="FileText" size={"17px"} color={activeTab === 'overview-tab' ? '#fff' : '#000'}  /> Overview</span>
                   </a>
                </li>
                {/* Content Tab: Document viewer and clauses */}
                <li className="nav-item">
                   <a className={`nav-link fs-14 ${activeTab === 'content' ? 'active' : ''}`} 
            data-bs-toggle="tab" 
            href="javascript:void(0)" 
            role="tab"
            onClick={() => handleTabChange('content')}
>
                   <i className="ri-list-unordered d-inline-block d-md-none"></i> <span className="d-none d-md-inline-block"><CommonIcon iconName="BookOpen" size={"17px"} color={activeTab === 'content' ? '#fff' : '#000'}  /> Content</span>
                   </a>
                </li>
             </ul>
             </div>
             </div>
                </div>
                <div className="row">
                        <div className="col-md-12">
                            <div className="card">
                                <div className="card-body">
                                <div className="row">
                                <div className="col-md-12">
                                <div>
          <div className="tab-content pt-0 text-muted">
             <div className={`tab-pane ${activeTab === 'overview-tab' ? 'active' : ''}`} id="overview-tab" role="tabpanel">
             <div className="card shadow-sm border-0 rounded-4">
  <div className="card-body p-4 d-none">
    <h5 className="fw-semibold mb-4 text-primary border-bottom pb-2">
      Contract Meta Data
    </h5>

    <div className="row g-3">
      <div className="col-lg-3 col-md-6">
        <div className="p-3 bg-light rounded-3 h-100">
          <div className="fw-semibold text-secondary small mb-1">
            {OVERVIEW_META_DATA["FilePath"]["label"]}
          </div>
          <div className="text-dark fw-medium text-truncate">
            {getFilenameFromURL(documents["FilePath"])}
          </div>
        </div>
      </div>

      <div className="col-lg-3 col-md-6">
        <div className="p-3 bg-light rounded-3 h-100">
          <div className="fw-semibold text-secondary small mb-1">
            {OVERVIEW_META_DATA["DocumentNo"]["label"]}
          </div>
          <div className="text-dark fw-medium">
            {documents["DocumentNo"]}
            </div>
        </div>
      </div>

      <div className="col-lg-3 col-md-6">
        <div className="p-3 bg-light rounded-3 h-100">
          <div className="fw-semibold text-secondary small mb-1">
            {OVERVIEW_META_DATA["documenttype"]["label"]}
          </div>
          <div className="text-dark fw-medium">
            {documents["documenttype"]}
            </div>
        </div>
      </div>

      <div className="col-lg-3 col-md-6">
        <div className="p-3 bg-light rounded-3 h-100">
          <div className="fw-semibold text-secondary small mb-1">
            {OVERVIEW_META_DATA["licensor"]["label"]}
          </div>
          <div className="text-dark fw-medium">
            {documents["payable_to"]}
            </div>
        </div>
      </div>

      <div className="col-lg-3 col-md-6">
        <div className="p-3 bg-light rounded-3 h-100">
          <div className="fw-semibold text-secondary small mb-1">
            {OVERVIEW_META_DATA["version"]["label"]}
          </div>
          <div className="text-dark fw-medium">1.0</div>
        </div>
      </div>

      <div className="col-lg-3 col-md-6">
        <div className="p-3 bg-light rounded-3 h-100">
          <div className="fw-semibold text-secondary small mb-1">
            {OVERVIEW_META_DATA["expirationdate"]["label"]}
          </div>
          <div className="text-dark fw-medium">
            {documents["expirationdate"]}
          </div>
        </div>
      </div>

      <div className="col-lg-3 col-md-6">
        <div className="p-3 bg-light rounded-3 h-100">
          <div className="fw-semibold text-secondary small mb-1">
            {OVERVIEW_META_DATA["effectivedate"]["label"]}
          </div>
          <div className="text-dark fw-medium">
            {documents["effectivedate"]}
          </div>
        </div>
      </div>

      <div className="col-lg-3 col-md-6">
        <div className="p-3 bg-light rounded-3 h-100">
          <div className="fw-semibold text-secondary small mb-1">
            {OVERVIEW_META_DATA["sell-off period"]["label"]}
          </div>
          <div className="text-dark fw-medium">
            {/* {data["items"]["sell-off period"]["value"]}{" "}
            {data["items"]["sell-off period"]["value"] !== "NA"
              ? OVERVIEW_META_DATA["sell-off period"]["units"]
              : ""} */} NA
              {documents["sell_off_period"]}
          </div>
        </div>
      </div>
    </div>
  </div>
  <div className="card-body p-4 bg-white shadow-sm rounded-4 border-0">
  <div className="d-flex align-items-center mb-4 border-bottom pb-2">
    <i className="bi bi-file-earmark-text text-primary me-2 fs-5"></i>
    <h5 className="fw-semibold text-primary mb-0">Contract Meta Data</h5>
  </div>

  <div className="row g-3">
    <div className="col-lg-3 col-md-6">
      <div className="meta-card p-3 rounded-4 h-100 shadow-sm">
        <div className="text-uppercase small text-muted mb-1" style={{ fontSize: "0.7rem",fontWeight: "600" }}>
          TITLE
        </div>
        <div className="fw-semibold text-dark text-truncate">
          {getFilenameFromURL(documents["FilePath"])}
        </div>
      </div>
    </div>

    <div className="col-lg-3 col-md-6">
      <div className="meta-card p-3 rounded-4 h-100 shadow-sm">
        <div className="text-uppercase small text-muted mb-1" style={{ fontSize: "0.7rem",fontWeight: "600" }}>
          DOCUMENT NO
        </div>
        <div className="fw-semibold text-dark">{documents["DocumentNo"]}</div>
      </div>
    </div>

    <div className="col-lg-3 col-md-6">
      <div className="meta-card p-3 rounded-4 h-100 shadow-sm">
        <div className="text-uppercase small text-muted  mb-1" style={{ fontSize: "0.7rem",fontWeight: "600" }}>
          DOCUMENT TYPE
        </div>
        <div className="fw-semibold text-dark">{documents["documenttype"]}</div>
      </div>
    </div>

    <div className="col-lg-3 col-md-6">
      <div className="meta-card p-3 rounded-4 h-100 shadow-sm">
        <div className="text-uppercase small text-muted mb-1" style={{ fontSize: "0.7rem",fontWeight: "600" }}>
          PARTY1
        </div>
        <div className="fw-semibold text-dark">{documents["payable_to"]}</div>
      </div>
    </div>

    <div className="col-lg-3 col-md-6">
      <div className="meta-card p-3 rounded-4 h-100 shadow-sm">
        <div className="text-uppercase small text-muted  mb-1" style={{ fontSize: "0.7rem",fontWeight: "600" }}>
          Version
        </div>
        <div className="fw-semibold text-dark">1.0</div>
      </div>
    </div>

    <div className="col-lg-3 col-md-6">
      <div className="meta-card p-3 rounded-4 h-100 shadow-sm">
        <div className="text-uppercase small text-muted mb-1" style={{ fontSize: "0.7rem",fontWeight: "600" }}>
          Expiry Date
        </div>
        <div className="fw-semibold text-dark">{documents["expirationdate"]}</div>
      </div>
    </div>

    <div className="col-lg-3 col-md-6">
      <div className="meta-card p-3 rounded-4 h-100 shadow-sm">
        <div className="text-uppercase small text-muted mb-1" style={{ fontSize: "0.7rem",fontWeight: "600" }}>
          Effective Date
        </div>
        <div className="fw-semibold text-dark">{documents["effectivedate"]}</div>
      </div>
    </div>

    <div className="col-lg-3 col-md-6">
      <div className="meta-card p-3 rounded-4 h-100 shadow-sm">
        <div className="text-uppercase small text-muted mb-1" style={{ fontSize: "0.7rem",fontWeight: "600" }}>
          Selloff Period
        </div>
        <div className="fw-semibold text-dark">{documents["sell_off_period"]}</div>
      </div>
    </div>
  </div>
</div>

</div>

             {
              tabledata.length > 0 ?
             <div className="card mt-3">
             <div className="card-body">
                            <h5 className="card-title mb-3">Contract Details</h5>
                            <div className="row">
                              {
                                 tabledata.length > 0 ? 
                                 <table
                                 id="contract_grid"
                                 className="display"
                                 style={{ width: "100%" }}
                               >
                               <thead>
                               </thead>
                               </table> : 
                                 <div className="col-lg-3 mb-2">No Details Found</div>

                              }
                            </div>
             </div>
             </div>
             : ""}
             </div>
             <div className={`tab-pane ${activeTab === 'content' ? 'active' : ''}`} id="content" role="tabpanel">

<div className="container-fluid">
  <div className="row g-4">
    {/* Document Viewer */}
    <div className="col-lg-8">
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title d-flex align-items-center mb-0">
            <i className="bi bi-file-earmark-text me-2"></i>
            Document Viewer
          </h5>
          <div className="d-flex gap-2">
            {/* Search button (commented out) */}
            {/* <button className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-search me-1"></i> Search
            </button> */}
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => {
                const viewer = document.getElementById("document-viewer");
                if (viewer?.requestFullscreen) {
                  viewer.requestFullscreen();
                }
              }}
            >
              <i className="bi bi-arrows-fullscreen me-1"></i> Full Screen
            </button>
          </div>
        </div>

        <div className="card-body">
          {(
            <div
              id="document-viewer"
              className="border rounded p-3"
              style={{
                minHeight: "600px",
                maxHeight: "600px",
                overflow: "auto",
                // whiteSpace: "pre-line",
                backgroundColor: "#fff",
              }}
            >
              <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {highlightContent(
                  displayContent,
                  documents.dynamicFields,
                  selectedClause,
                  selectedClauseArrayIndex
                )}
              </Markdown>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Right Sidebar */}
    <div className="col-lg-4">
      <div className="card shadow-sm">
        <div className="card-header p-2">
          <h5 className="card-title d-flex align-items-center mb-1 ms-0">
            <i className="bi bi-tag me-2"></i>
            Clause Summary
          </h5>
          <small className="text-muted ms-0" style={{ paddingLeft: "8px" }}>
  Click any clause to highlight it in the document
</small>

        </div>
        <div className="card-body" style={{ maxHeight: "630px", overflowY: "auto" }}>
          {/* {documents.dynamicFields &&
            Object.entries(documents.dynamicFields).map(([key, value]) => (
              <div
                key={key}
                className={`p-3 border rounded mb-2 cursor-pointer ${
                  selectedClause === key
                    ? "bg-primary-subtle border-primary"
                    : "bg-white"
                }`}
                onClick={() => handleClauseClick(key)}
                style={{ transition: "background-color 0.2s ease-in-out" }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h6 className="fw-semibold text-dark mb-1">{key}</h6>
                    <p className="text-primary mb-0">
                      {value || "—"}
                    </p>
                  </div>
                  {selectedClause === key && (
                    <i className="bi bi-highlighter text-primary"></i>
                  )}
                </div>
              </div>
            ))} */}

            {/* {documents.dynamicFields &&
  Object.entries(documents.dynamicFields).map(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    return (
      <div
        key={key}
        className={`p-3 border rounded mb-2 cursor-pointer ${
          selectedClause === key
            ? "bg-primary-subtle border-primary"
            : "bg-white"
        }`}
       
        style={{ transition: "background-color 0.2s ease-in-out" }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <h6 className="fw-semibold text-dark mb-2">{key}</h6>

            {values.length === 1 ? (
              <p onClick={() => handleClauseClick(key,0)}  className="text-primary mb-0">{values[0] || "—"}</p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {values.map((v, idx) => (
                  <div
                   onClick={() => handleClauseClick(key,idx)}
                    key={idx}
                    className="text-primary small bg-light border rounded px-2 py-1"
                  >
                    {v}
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedClause === key && (
            <i className="bi bi-highlighter text-primary"></i>
          )}
        </div>
      </div>
    );
  })} */}


         
{documents.dynamicFields &&
  Object.entries(documents.dynamicFields).map(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    const isExpanded = expandedClause === key;
    const hasMultiple = values.length > 1;

    return (
      <div
        key={key}
        className={`p-3 mb-3 rounded-4 border ${
          selectedClause?.key === key
            ? "border-primary bg-light"
            : "border-light bg-white"
        } shadow-sm`}
        style={{
          transition: "all 0.25s ease",
          boxShadow:
            selectedClause?.key === key
              ? "0 2px 10px rgba(0, 123, 255, 0.2)"
              : "0 1px 5px rgba(0,0,0,0.05)",
        }}
      >
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center">
          <div
            className="d-flex align-items-center gap-2 cursor-pointer"
            onClick={() => handleExpandToggle(key)}
          >
            <CommonIcon
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              size="18px"
            />
            <h6
              className="fw-semibold text-dark mb-0"
              style={{
                letterSpacing: "0.3px",
                fontSize: "0.95rem",
                userSelect: "none",
              }}
            >
              {key}
            </h6>
          </div>
          <span
            className="badge rounded-pill"
            style={{
              fontSize: "0.8rem",
              fontWeight: 500,
              background: "rgb(100, 116, 139)",
            }}
          >
            {values.length}
          </span>
        </div>

        {/* Expandable Section */}
        {isExpanded && (
          <div
            className="mt-3"
            style={{
              animation: "fadeIn 0.25s ease-in-out",
              marginLeft: 0,
            }}
          >
            {values.map((v, idx) => {
              const isSelected =
                selectedClause === key &&
                selectedClauseArrayIndex === idx;

              // Show border only if multiple values and not last
              const showBorder = hasMultiple && idx !== values.length - 1;

              return (
                <div
                  key={idx}
                  onClick={() => handleClauseClick(key, idx)}
                  className={`d-flex align-items-center py-2 px-2 w-100 ${
                    isSelected
                      ? "bg-primary-subtle text-dark"
                      : "bg-transparent text-dark"
                  }`}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    borderBottom: showBorder
                      ? "1px solid #e6e6e6"
                      : "1px solid transparent",
                    // borderRadius: "8px",
                  }}
                >
                  {/* Highlighter indicator */}

                  {isSelected ? 
                  <CommonIcon iconName="Highlighter" size={"14px"} color={isSelected ? "#333" : "#333"}  />
                  : ""}

                  {/* Text content */}
                  <div
                    style={{
                      flex: 1,
                      color: isSelected ? "#333" : "#333",
                      transition: "color 0.25s ease",
                    }}
                  >
                    &nbsp;{v}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  })}



   




        </div>
      </div>
    </div>
  </div>
</div>




                <div className="row d-none">
                    <div className="col-lg-8">
                     {/* {'https://mozilla.github.io/pdf.js/web/viewer.html?file='} */}
                     {
                        iframeSrc==="" ?   
                        <p className="center">No PDF URL Found</p>
                        :
                   <>
                        {/* <iframe 
                    id="loadPdf" style={{display:"block"}} 
                    src={iframeSrc}
                    width="100%"
                    height="960" 
                    type="application/pdf"></iframe> */}
                    {console.log("******************")}
                    {console.log(documents.dynamicFields)}
                     {/* selectedClause=      */}
                    <Markdown
  >
        {highlightContent(displayContent, documents.dynamicFields, "__th day of October, 2012")}
      </Markdown>
      </>

                     }
                    </div>
                    <div className="col-lg-4">
                    <div class="panel panel-default">
                    <div class="panel-heading">
                    <ul className="nav nav-pills content-inner-tab gap-2 gap-lg-3 flex-grow-1" role="tablist">
                    <li className="nav-item">
                    <a href='javascript:void(0)' className={`nav-link fs-14 ${activeInnerTab === 'show-clauses-tab' ? 'active' : ''}`}
                        onClick={() => handleInnerTabChange('show-clauses-tab')}
>
                    <i className="ri-airplay-fill d-inline-block d-md-none"></i> <span className="d-none d-md-inline-block"><CommonIcon iconName="BookOpen" size={"17px"} color="#000"  /> Show Clauses</span>
                    </a>
                    </li>
                    <li className="nav-item ask-genie-fullwidth">
                    <a href='javascript:void(0)' className={`nav-link fs-14 ${activeInnerTab === 'askgenie-tab' ? 'active' : ''}`}
                        onClick={() => handleInnerTabChange('askgenie-tab')}
                        role="tab"
>
                    <i className="ri-list-unordered d-inline-block d-md-none"></i> <span className="d-none d-md-inline-block"><CommonIcon iconName="WandSparkles" size={"17px"} color="#000"  /> AskGenie</span>
                    </a>
                    </li>
                    </ul>
                    </div>
                    <div class="panel-body">
                    <div className="tab-content text-muted">
                    <div className={`tab-pane ${activeInnerTab === 'show-clauses-tab' ? 'active' : ''}`} id="show-clauses-tab" role="tabpanel">
                    {/* <div className="accordion custom-accordionwithicon accordion-fill-primary" id="accordionFill">
                                  {Object.entries(data.items).map(([key, item]) => (
                                     <div className="accordion-item material-shadow">
                                     <h2 className="accordion-header" id={key}>
                                     <button
                                                       className={`accordion-button ${openItem === key ? '' : 'collapsed'}`}
                                                       type="button"
                                                       onClick={() => toggleAccordion(key)}
                                                       aria-expanded={openItem === key ? 'true' : 'false'}
                                                       aria-controls={"content_" + key}
                                         
                                          >
                                         {(OVERVIEW_META_DATA[key] && typeof OVERVIEW_META_DATA[key].label === 'string') 
                                          ? capitalizeFirstLetterOfEachWord(`${OVERVIEW_META_DATA[key].label} (${item.sentences.length})`)
                                          : capitalizeFirstLetterOfEachWord(`${key} (${item.sentences.length})`)}


                                       </button>
                                     </h2>
                                    <div 
                                                id={"content_" + key}
                                                className={`accordion-collapse collapse ${openItem === key ? 'show' : ''}`}
                                                aria-labelledby={key}
                                                data-bs-parent="#accordionFill"
                                    >
                                    <div className="accordion-body">
                                    {item.sentences.length > 0 ? (
                                        <div>
                                        {item.sentences.map((sentence, index) => (
                                        <>  
                                        <p className="text-muted my-2 ff-secondary">{index+1}.{cutString(sentence,60)}</p>
                                        <button 
                                        onClick={() => highlightWord(sentence)} // Pass a function reference
                                        type="button" className="btn btn-primary btn-sm mb-2">Click to view contract clause</button> 
                                        </>  
                                        ))}
                                        </div>) : (
                                        <p className="text-muted my-2 ff-secondary">No sentences available</p>
                                        )}
                                    </div>
                                    </div>
                                 </div>
                                  ))}
                    </div> */}
                    </div>
                    <div className={`tab-pane ${activeInnerTab === 'askgenie-tab' ? 'active' : ''}`}  id="askgenie-tab" role="tabpanel">

                    <div id="frame">
                  <div className="content">
                    

                        {conversationHistory.length === 0  ?
                        <div className="askgenie-centered-container text-center">
                              <div className="mb-5">
                              <CommonIcon iconName="Sparkles" size={"55px"} color="#a25ddc"  />
                              </div>
                              <h5 className="mb-3" >Contract Intelligence at Your Service</h5>
                              <p>Ask me anything about this document. I can help you understand clauses, extract key information, or analyze specific sections.</p>
                        </div> : ""
                        }
                  


                     <div className="messages" ref={messagesContainerRef}>
                    <ul>
                        {conversationHistory.map((msg, index) => (
                            <li key={index} className={msg.type}>
                                <p>{msg.message}</p>
                            </li>
                        ))}
   {/* Loader while waiting for the API response */}
   {loading && (
<li className="loading">
    <div className="loader">
    <CommonIcon iconName="RefreshCcw" size={"17px"} color="#fff"  />
    </div>
</li>
)}
                     
                    </ul>
                </div>

                <div className="message-input">
                    <div className="wrap">
                   
                   <div className='row'>
                              {/* <div className='col-md-1'>
                              <CommonIcon iconName="RefreshCw " size={"30px"} color="#000"  />
                              </div> */}
                              <div className='col-md-10'>
                        {/* <input
                            type="text"
                            className='form-control'
                            placeholder="Ask me anything about this document"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                        /> */}
                        <textarea 
                        className='form-control'
                        placeholder="Ask me anything about this document"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault(); // Prevents a new line from being added
                            handleSendMessage();
                          }
                        }}
              
                        ></textarea>
                        </div>
                        <div className='col-md-1'>
                        <button className="btn btn-primay sendmessage" onClick={handleSendMessage}>
                              <CommonIcon iconName="WandSparkles" size={"17px"} color="#fff"  />
                        </button>
                        </div>
                        </div>
                        <br/>
                    </div>
                </div>
            </div>
        </div>
                    </div>
                    </div>
                    </div>
                    </div>
                    </div>
                    </div>
             </div>
          </div>
       </div>
    </div>
</div>    
                                </div>
                            </div>
                        </div>
                </div>
                </>
                ) : (loader())}
                </>
            </div>
        </div>
    </div>
   )
}